import React, { useEffect, useState } from 'react';
import { Col, Row, Card } from 'react-bootstrap';
import { getAllHVAAgents, HVAAgent } from 'services/hvaagent.service';
import { hvaImagesandVoices } from 'pages/hva/hvaImagesandVoices'; // Ajusta la ruta según sea necesario
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import HVADynamicConversationModal from 'pages/hva/ServGlobalCaja/HVADynamicConversationModal';
import spanish from 'assets/img/flags/spain.png'; // Importar banderas
import english from 'assets/img/flags/england.png';
import greek from 'assets/img/flags/greece.png';
import mexican from 'assets/img/flags/mexico.png';
import german from 'assets/img/flags/germany.png';
import brasilian from 'assets/img/flags/brazil.png';
import italian from 'assets/img/flags/italy.png';
import chilean from 'assets/img/flags/chile.png';
import turkish from 'assets/img/flags/turkey.png';
import colombian from 'assets/img/flags/colombia.png';

type Language =
  | 'spanish'
  | 'english'
  | 'greek'
  | 'mexican'
  | 'german'
  | 'brasilian'
  | 'italian'
  | 'chilean'
  | 'turkish'
  | 'colombian';

const HVAServGlobalCaja = () => {
  const [agents, setAgents] = useState<HVAAgent[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<HVAAgent | null>(null);

  useEffect(() => {
    const fetchAgents = async () => {
      const agentsData = await getAllHVAAgents();
      const filteredAgents = agentsData
        // eslint-disable-next-line
        .filter(agent => ['Victoria'].includes(agent.name))
        .sort((a, b) => {
          const order = ['Victoria'];
          return order.indexOf(a.name) - order.indexOf(b.name);
        });
      setAgents(filteredAgents);
    };
    fetchAgents();
  }, []);

  const getImagePath = (bxpname: string): string | undefined => {
    return hvaImagesandVoices[bxpname]?.image;
  };

  const languageToFlag: Record<Language, string> = {
    spanish: spanish,
    english: english,
    greek: greek,
    mexican: mexican,
    german: german,
    brasilian: brasilian,
    italian: italian,
    chilean: chilean,
    turkish: turkish,
    colombian: colombian
  };

  const handleConversationError = (error: Error) => {
    // Handle conversation errors appropriately
    console.error('Conversation error:', error);
    // To show a toast or notification here
  };

  const handleOpenChat = (agent: HVAAgent) => {
    setSelectedAgent(agent);
    setShowModal(true);
  };

  const handleCloseChat = () => {
    setSelectedAgent(null);
    setShowModal(false);
  };

  return (
    <>
      <Row className="gy-3 mb-6 justify-content-between">
        <Col>
          <h2 className="mb-2 text-body-emphasis">Globalcaja</h2>
          <h5 className="text-body-tertiary fw-semibold">
            Humanized Virtual Agent (HVA) playground.
          </h5>
        </Col>
      </Row>
      <Row className="d-flex p-0">
        {agents.map(agent => (
          <Col key={agent.id} sm={12} xxl={12} className="pb-3">
            <Card className="h-100 position-relative">
              <Card.Body className="p-6 pt-2">
                <img
                  src={languageToFlag[agent.language as Language] || spanish}
                  alt="Language"
                  className="position-absolute"
                  style={{ top: '0px', right: '0px' }}
                />
                <Row>
                  <Col>
                    <h3 className="text-body-emphasis pt-5 pb-3">
                      {`${agent.name}'s Service Summary`}
                    </h3>
                  </Col>
                  <Col className="d-flex align-items-center justify-content-end pe-6">
                    {agent.agentcode && (
                      <div
                        className="text-center bg-primary rounded-5"
                        style={{ maxWidth: '180px', width: '180px' }}
                      >
                        <Button
                          variant="link"
                          className="p-2 text-decoration-none text-white"
                          onClick={() => handleOpenChat(agent)}
                        >
                          {`Test ${agent.name}`}
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="px-1 pe-0"
                          />
                        </Button>
                      </div>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col
                    sm="auto"
                    xxl="auto"
                    className="d-flex align-items-center justify-content-center p-4 pt-1 pb-0"
                  >
                    {getImagePath(agent.bxpname) ? (
                      <img
                        src={getImagePath(agent.bxpname)}
                        alt={`${agent.name}`}
                      />
                    ) : (
                      <p>Image not found</p>
                    )}
                  </Col>
                  <Col className="d-flex flex-column p-4 fs-9 lh-sm pb-0">
                    <div>
                      <p className="fs-9">
                        Victoria is a virtual agent for Globalcaja bank's
                        pre-approved loans department, based in the central
                        offices in Spain. Her main role is to make outbound
                        calls to pre-selected customers who have been
                        pre-approved for specific loans.
                      </p>

                      <h6>Key Aspects of Service</h6>
                      <ul>
                        <li>
                          Informs customers about their pre-approved loan that
                          was previously communicated via email and is available
                          in their online banking
                        </li>
                        <li>
                          Only provides information about the specific
                          pre-approved loan type assigned to each customer
                        </li>
                        <li>
                          Highlights the loan's advantages, including no
                          commissions and flexible cancellation terms
                        </li>
                        <li>
                          Explores the customer's loan needs through examples of
                          potential uses (renovations, travel, studies, etc.)
                        </li>
                        <li>
                          Provides loan variations based on a predefined list
                          matching the customer's assigned loan type
                        </li>
                      </ul>

                      <h6>Contracting Channels</h6>
                      <ul>
                        <li>Directly over the phone during the call</li>
                        <li>
                          Through the customer's online banking (Ruralvía)
                        </li>
                        <li>At their local branch</li>
                      </ul>

                      <h6>Phone Verification Protocol</h6>
                      <ul>
                        <li>
                          Confirmation of full name, ID number verification and
                          Date of birth verification
                        </li>
                      </ul>

                      <h6>Communication Guidelines</h6>
                      <ul>
                        <li>
                          Maintains formal language and professional distance
                        </li>
                        <li>Must inform about call recording</li>
                        <li>
                          Can only provide information from the pre-approved
                          loan list
                        </li>
                        <li>Must summarize loan terms before final approval</li>
                      </ul>

                      <p>
                        The service focuses on customer convenience while
                        maintaining security and compliance with banking
                        protocols. Victoria can handle basic queries about the
                        pre-approved loan but must redirect other banking
                        questions to branches or online banking.
                      </p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {selectedAgent && (
        <HVADynamicConversationModal
          show={showModal}
          onHide={handleCloseChat}
          agentCode={selectedAgent.agentcode}
          agentName={selectedAgent.name}
          agentImage={getImagePath(selectedAgent.bxpname)}
          onError={handleConversationError}
          customer="Alberto García"
          dni="51826572B"
          dateOfBirth={new Date('1980-11-13')}
          loanType={3}
        />
      )}
    </>
  );
};

export default HVAServGlobalCaja;
